// Helpers for the per-app contact list (applicant, repeat contact, co-borrowers)
// returned by the backend as `contacts`. A missing list, or one without any
// co-borrower, means "single applicant" and callers should render as before.

// `repeat` is the applicant's older duplicate GHL contact, so it reads as the applicant.
export const CONTACT_ROLE_LABELS = {
    primary: "Applicant",
    repeat: "Applicant",
    co_borrower: "Co-borrower",
};

export const normalizeContacts = (contacts) => {
    if (!Array.isArray(contacts)) return [];
    const seen = new Set();
    return contacts.filter((c) => {
        if (!c?.ghl_contact_id || seen.has(c.ghl_contact_id)) return false;
        seen.add(c.ghl_contact_id);
        return true;
    });
};

export const hasCoBorrowerContact = (contacts) =>
    normalizeContacts(contacts).some((c) => c.role === "co_borrower");

export const findContact = (contacts, ghlContactId) =>
    normalizeContacts(contacts).find((c) => c.ghl_contact_id === ghlContactId) || null;

export const contactDisplayName = (person) =>
    person?.name || person?.phone || person?.email || "Unknown";

// "Luis Diaz (Co-borrower)"; ids missing from `contacts` fall back to the raw id
export const contactLabel = (contacts, ghlContactId, { withRole = true } = {}) => {
    const contact = findContact(contacts, ghlContactId);
    if (!contact) return ghlContactId || "Unknown";
    const role = CONTACT_ROLE_LABELS[contact.role];
    return withRole && role ? `${contactDisplayName(contact)} (${role})` : contactDisplayName(contact);
};

export const ghlContactUrl = (locationId, ghlContactId) =>
    `https://phone.bcrumbs.com/v2/location/${locationId}/contacts/detail/${ghlContactId}`;

// Who a call was with, for feeds that mix applicant and co-borrower calls.
// Null unless the app has a linked co-borrower and the call's contact is in
// `contacts`, so single-applicant apps and older responses render as before.
export const callParticipant = (contacts, ghlContactId) => {
    if (!ghlContactId || !hasCoBorrowerContact(contacts)) return null;
    const contact = findContact(contacts, ghlContactId);
    if (!contact) return null;
    return {
        name: contactDisplayName(contact),
        role: CONTACT_ROLE_LABELS[contact.role] || null,
        isCoBorrower: contact.role === "co_borrower",
    };
};
